import { useEffect, useMemo, useState } from 'react';
import { formatCurrency } from '@vanilla/utils/Utils.js';
import { getDealForecastValue } from '@vanilla/utils/ForecastUtils.js';
import { sumRevenueRecognitionAmount, getRevenueRecognitionForecast } from '@vanilla/utils/CrmDealFilters.js';
import { CrmApiRepository } from '@vanilla/services/crm/CrmApiRepository.js';
import { GoalService } from '@vanilla/services/GoalService.js';
import { permissions } from '@vanilla/services/PermissionService.js';
import { buildFilterState } from '../lib/period.js';

function isClosedLost(deal) {
    return deal?.dealStage === 'closedLost' || deal?.dealStage === 'closed_lost' || deal?.status === 'lost' || deal?.dealStage === 'lost';
}
function isClosedWon(deal) {
    return deal?.dealStage === 'closed' || deal?.dealStage === 'closed_won' || deal?.status === 'won' || deal?.dealStage === 'won';
}
function getGoalRange(goal) {
    if (!goal) return null;
    if (goal.startDate && goal.endDate) {
        return { start: new Date(goal.startDate), end: new Date(goal.endDate) };
    }
    const y = Number(goal.year) || new Date().getFullYear();
    if (goal.period === 'monthly' && goal.month) {
        return { start: new Date(y, Number(goal.month) - 1, 1), end: new Date(y, Number(goal.month), 0) };
    }
    if (goal.period === 'quarterly' && goal.quarter) {
        const q = Number(goal.quarter);
        return { start: new Date(y, (q - 1) * 3, 1), end: new Date(y, q * 3, 0) };
    }
    return { start: new Date(y, 0, 1), end: new Date(y, 11, 31) };
}
function sumClosedWonForGoal(goal, deals) {
    const range = getGoalRange(goal);
    if (!range) return 0;
    let sum = 0;
    deals.forEach((d) => {
        if (!isClosedWon(d)) return;
        const ds = d.actualCloseDate || d.expectedCloseDate || d.dealDate || d.updatedAt || d.createdAt;
        if (!ds) return;
        const dt = new Date(ds);
        if (Number.isNaN(dt.getTime())) return;
        if (dt < range.start || dt > range.end) return;
        sum += Number(d.dealAmount || d.amount || d.contractValue || 0);
    });
    return sum;
}

export function useDealSummary(deals, filters = {}, schedules = null) {
    const [summaries, setSummaries] = useState({
        totalAmount: 0,
        totalForecast: 0,
        revenueReg: 0,
        revenueRegForecast: 0,
    });
    const [orgGoals, setOrgGoals] = useState({ breakthrough: null, profitable: null });

    const canViewFinancials = permissions.canViewFinancials('deals');
    const isRevenueMode = (filters.dimension || 'created') === 'revenue';
    const builtFilters = useMemo(() => buildFilterState(filters), [filters]);

    useEffect(() => {
        if (!canViewFinancials) {
            setOrgGoals({ breakthrough: null, profitable: null });
            return;
        }
        let cancelled = false;
        const fetchGoals = async () => {
            try {
                const all = await GoalService.getAll().catch(() => []);
                if (cancelled) return;
                const pick = (type) => {
                    const list = all.filter((g) => g.goalType === type && (g.scope === 'organization' || !g.userId) && g.status !== 'inactive');
                    if (!list.length) return null;
                    list.sort((a, b) => (b.year || 0) - (a.year || 0) || (b.targetValue || 0) - (a.targetValue || 0));
                    return list[0];
                };
                const bRaw = pick('breakthrough');
                const pRaw = pick('profitable');
                const calc = (g) => {
                    if (!g) return null;
                    const target = Number(g.targetValue ?? 0);
                    const achieved = sumClosedWonForGoal(g, deals);
                    const pct = target > 0 ? Math.min(100, Math.round((achieved / target) * 100)) : 0;
                    return { ...g, targetValue: target, achievedValue: achieved, progress: pct };
                };
                if (!cancelled) setOrgGoals({ breakthrough: calc(bRaw), profitable: calc(pRaw) });
            } catch {
                if (!cancelled) setOrgGoals({ breakthrough: null, profitable: null });
            }
        };
        fetchGoals();
        const handler = () => fetchGoals();
        window.addEventListener('goalSaved', handler);
        window.addEventListener('goalDeleted', handler);
        window.addEventListener('dealSaved', handler);
        window.addEventListener('dealDeleted', handler);
        return () => {
            cancelled = true;
            window.removeEventListener('goalSaved', handler);
            window.removeEventListener('goalDeleted', handler);
            window.removeEventListener('dealSaved', handler);
            window.removeEventListener('dealDeleted', handler);
        };
    }, [canViewFinancials, deals]);

    useEffect(() => {
        if (!canViewFinancials) {
            setSummaries({ totalAmount: 0, totalForecast: 0, revenueReg: 0, revenueRegForecast: 0 });
            return;
        }

        let cancelled = false;

        const run = async () => {
            let totalAmount = 0;
            let totalForecast = 0;
            deals.forEach((d) => {
                if (isClosedLost(d)) return;
                totalAmount += Number(d.dealAmount || d.amount || d.contractValue || 0);
                totalForecast += Number(d.forecastAmount ?? getDealForecastValue(d));
            });

            const next = { totalAmount, totalForecast, revenueReg: 0, revenueRegForecast: 0 };

            if (isRevenueMode) {
                try {
                    const allSchedules = schedules && schedules.length > 0
                        ? schedules
                        : await CrmApiRepository.getAllSchedules();
                    let revenueReg = 0;
                    let revenueRegForecast = 0;
                    deals.forEach((d) => {
                        if (isClosedLost(d)) return;
                        const amount = d.revenueRecognitionAmount ?? sumRevenueRecognitionAmount(d, allSchedules, builtFilters);
                        const forecast = d.revenueRecognitionForecast ?? getRevenueRecognitionForecast(d, amount);
                        revenueReg += amount;
                        revenueRegForecast += forecast;
                    });
                    next.revenueReg = revenueReg;
                    next.revenueRegForecast = revenueRegForecast;
                } catch (err) {
                    console.warn('[useDealSummary] revenue recognition computation failed', err);
                }
            }

            if (cancelled) return;
            setSummaries(next);
        };

        run();
        return () => {
            cancelled = true;
        };
    }, [deals, canViewFinancials, isRevenueMode, builtFilters, schedules]);

    const goalView = (g) => {
        if (!g) return { hasGoal: false, goalAmount: formatCurrency(0, 0), achievedAmount: formatCurrency(0, 0), pct: 0, goal: null };
        return {
            hasGoal: true,
            goalAmount: formatCurrency(Number(g.targetValue) || 0, 0),
            achievedAmount: formatCurrency(Number(g.achievedValue) || 0, 0),
            pct: Number(g.progress) || 0,
            goal: g,
        };
    };

    const display = useMemo(() => {
        return {
            totalAmount: formatCurrency(summaries.totalAmount, 0),
            totalForecast: formatCurrency(summaries.totalForecast, 0),
            revenueReg: formatCurrency(summaries.revenueReg, 0),
            revenueRegForecast: formatCurrency(summaries.revenueRegForecast, 0),
            revenueMode: isRevenueMode,
            canViewFinancials,
            breakthrough: goalView(orgGoals.breakthrough),
            profitable: goalView(orgGoals.profitable),
        };
    }, [summaries, isRevenueMode, canViewFinancials, orgGoals]);

    return display;
}